import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import {
  LinkedInOutlineIcon,
  FacebookOutlineIcon,
  XOutlineIcon,
  InstagramOutlineIcon,
} from "./outlineSocialIcons";
import { HEADER } from "./headerTokens";

const SOCIAL_ICON_SIZE = 18;

const META_ICON_SX = {
  fontSize: HEADER.iconSize,
  color: "inherit",
  display: "block",
  flexShrink: 0,
};

const SOCIAL_ICON_SX = {
  fontSize: SOCIAL_ICON_SIZE,
  color: "inherit",
  display: "block",
};

const ICON_HIT_SX = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  lineHeight: 0,
  color: "text.secondary",
  textDecoration: "none",
  p: 0,
  border: 0,
  background: "none",
  cursor: "pointer",
  font: "inherit",
  transition: "color 0.15s ease",
  "&:hover": { color: "primary.main" },
};

/** Same unavailable treatment previously used for missing social profile icons. */
const ICON_DISABLED_SX = {
  ...ICON_HIT_SX,
  opacity: 0.4,
  cursor: "default",
  "&:hover": { color: "text.secondary" },
  "&.Mui-disabled, &:disabled": {
    opacity: 0.4,
    color: "text.secondary",
  },
};

function pickUrl(...candidates) {
  for (const value of candidates) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return undefined;
}

/**
 * Company payloads may send `social` as an object or an array of objects
 * (schema is arrayOf). Merge into a single lookup map.
 */
export function normalizeSocialLinks(social) {
  if (!social) return {};

  const entries = Array.isArray(social) ? social : [social];
  const merged = {};

  entries.forEach((entry) => {
    if (!entry || typeof entry !== "object") return;
    merged.linkedinUrl = pickUrl(
      merged.linkedinUrl,
      entry.linkedinUrl,
      entry.linkedin_url,
      entry.linkedin
    );
    merged.facebookUrl = pickUrl(
      merged.facebookUrl,
      entry.facebookUrl,
      entry.facebook_url,
      entry.facebook
    );
    merged.twitterUrl = pickUrl(
      merged.twitterUrl,
      entry.twitterUrl,
      entry.twitter_url,
      entry.twitter,
      entry.xUrl,
      entry.x_url
    );
    merged.instagramUrl = pickUrl(
      merged.instagramUrl,
      entry.instagramUrl,
      entry.instagram_url,
      entry.instagram
    );
  });

  return merged;
}

const SOCIAL_PLATFORM_DEFS = [
  { key: "linkedin", urlKey: "linkedinUrl", label: "LinkedIn", Icon: LinkedInOutlineIcon },
  { key: "facebook", urlKey: "facebookUrl", label: "Facebook", Icon: FacebookOutlineIcon },
  { key: "twitter", urlKey: "twitterUrl", label: "X", Icon: XOutlineIcon },
  { key: "instagram", urlKey: "instagramUrl", label: "Instagram", Icon: InstagramOutlineIcon },
];

export function getAvailableSocials(social) {
  const links = normalizeSocialLinks(social);
  return SOCIAL_PLATFORM_DEFS
    .map(({ key, urlKey, label, Icon }) => ({
      key,
      label,
      Icon,
      href: links[urlKey],
    }))
    .filter((item) => Boolean(item.href));
}

function websiteHref(website) {
  if (!website) return undefined;
  return website.includes("http") ? website : `https://${website}`;
}

function websiteLabel(website) {
  if (!website) return "";
  return String(website).replace(/^https?:\/\//i, "").replace(/\/$/, "");
}

/**
 * Website-only link under company identity (legacy text link).
 */
export function CompanyWebsiteLink({ website }) {
  const href = websiteHref(website);
  const label = websiteLabel(website);
  if (!href) return null;

  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      underline="always"
      data-testid="company-website-link"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: HEADER.gap.iconText,
        fontSize: 13,
        fontWeight: 400,
        lineHeight: 1.2,
        color: "text.secondary",
        textUnderlineOffset: "2px",
        textDecorationColor: "rgba(88, 89, 91, 0.4)",
        "&:hover": {
          color: "primary.main",
          textDecorationColor: "currentColor",
        },
      }}
    >
      <LanguageOutlinedIcon sx={{ fontSize: HEADER.iconSize, color: "inherit" }} />
      {label}
    </Link>
  );
}

CompanyWebsiteLink.propTypes = {
  website: PropTypes.string,
};

function MetaIconButton({ href, label, testId, onClick, children }) {
  const content = href ? (
    <Box
      component="a"
      href={href}
      target={href.startsWith("mailto:") ? undefined : "_blank"}
      rel={href.startsWith("mailto:") ? undefined : "noopener noreferrer"}
      aria-label={label}
      data-testid={testId}
      sx={ICON_HIT_SX}
    >
      {children}
    </Box>
  ) : (
    <Box
      component="button"
      type="button"
      aria-label={label}
      data-testid={testId}
      onClick={onClick}
      sx={{ ...ICON_HIT_SX, cursor: onClick ? "pointer" : "default" }}
    >
      {children}
    </Box>
  );

  return (
    <Tooltip title={label} arrow placement="top">
      {content}
    </Tooltip>
  );
}

MetaIconButton.propTypes = {
  href: PropTypes.string,
  label: PropTypes.string.isRequired,
  testId: PropTypes.string,
  onClick: PropTypes.func,
  children: PropTypes.node.isRequired,
};

/**
 * Always-visible Socials trigger beside Website / Location.
 * Active when profiles exist; disabled + tooltip when none.
 */
export function CompanySocialsMenu({ social }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const available = getAvailableSocials(social);
  const hasSocials = available.length > 0;
  const open = Boolean(anchorEl);

  const handleToggle = (event) => {
    if (!hasSocials) return;
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl((current) => (current ? null : event.currentTarget));
  };

  const handleClose = (event, reason) => {
    // Let the trigger button own toggle-close; avoid reopen race with backdrop.
    if (
      reason === "backdropClick" &&
      event?.target?.closest?.('[data-testid="company-socials-trigger"]')
    ) {
      return;
    }
    setAnchorEl(null);
  };

  const triggerButton = (
    <Box
      component="button"
      type="button"
      aria-label={hasSocials ? "Socials" : "No social profiles found"}
      aria-controls={open ? "company-socials-menu" : undefined}
      aria-haspopup={hasSocials ? "menu" : undefined}
      aria-expanded={hasSocials ? (open ? "true" : "false") : undefined}
      aria-disabled={hasSocials ? undefined : "true"}
      data-testid="company-socials-trigger"
      data-available={hasSocials ? "true" : "false"}
      onClick={handleToggle}
      disabled={!hasSocials}
      sx={hasSocials ? ICON_HIT_SX : ICON_DISABLED_SX}
    >
      <ShareOutlinedIcon sx={META_ICON_SX} />
    </Box>
  );

  return (
    <>
      <Tooltip
        title={hasSocials ? "Socials" : "No social profiles found"}
        arrow
        placement="top"
        disableHoverListener={open}
      >
        {/* Span keeps tooltip hover working when the button is disabled. */}
        <Box component="span" sx={{ display: "inline-flex", lineHeight: 0 }}>
          {triggerButton}
        </Box>
      </Tooltip>
      {hasSocials ? (
        <Menu
          id="company-socials-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          MenuListProps={{
            dense: true,
            "aria-label": "Socials",
            sx: { py: 0.5, minWidth: 168 },
          }}
          PaperProps={{
            elevation: 0,
            "data-testid": "company-socials-menu",
            sx: {
              mt: 0.75,
              borderRadius: "10px",
              border: HEADER.border,
              boxShadow: "0 8px 24px rgba(23, 15, 74, 0.10)",
              bgcolor: "background.paper",
            },
          }}
        >
          <Typography
            component="div"
            sx={{
              px: 1.5,
              pt: 0.75,
              pb: 0.35,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: "text.secondary",
            }}
          >
            Socials
          </Typography>
          {available.map(({ key, href, label, Icon }) => (
            <MenuItem
              key={key}
              component="a"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setAnchorEl(null)}
              data-testid={`company-social-link-${key}`}
              sx={{
                gap: 0.5,
                py: 0.85,
                px: 1.5,
                borderRadius: "6px",
                mx: 0.5,
                "&:hover": {
                  bgcolor: "rgba(74, 44, 245, 0.06)",
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 28, color: "text.secondary" }}>
                <Icon sx={SOCIAL_ICON_SX} />
              </ListItemIcon>
              <ListItemText
                primary={label}
                primaryTypographyProps={{
                  sx: { fontSize: 13, fontWeight: 500, color: "text.primary" },
                }}
              />
              <OpenInNewOutlinedIcon sx={{ fontSize: 14, color: "text.secondary", ml: 1 }} />
            </MenuItem>
          ))}
        </Menu>
      ) : null}
    </>
  );
}

CompanySocialsMenu.propTypes = {
  social: PropTypes.oneOfType([
    PropTypes.shape({
      linkedinUrl: PropTypes.string,
      facebookUrl: PropTypes.string,
      twitterUrl: PropTypes.string,
      instagramUrl: PropTypes.string,
    }),
    PropTypes.array,
  ]),
};

/**
 * Icon-only website / email / location / socials buttons beside the company name.
 * Website / email / location render only when data exists.
 * Socials always renders (active or unavailable) so header layout stays stable.
 */
export default function CompanyLinksRow({ website, email, locationLabel, social }) {
  const href = websiteHref(website);
  const label = websiteLabel(website);

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: HEADER.gap.socialIcons,
        flexShrink: 0,
        ml: 0.75,
      }}
      data-testid="company-links-row"
    >
      {href ? (
        <MetaIconButton href={href} label={label || "Website"} testId="company-website-link">
          <LanguageOutlinedIcon sx={META_ICON_SX} />
        </MetaIconButton>
      ) : null}

      {email ? (
        <MetaIconButton href={`mailto:${email}`} label={email} testId="company-email-link">
          <MailOutlineIcon sx={META_ICON_SX} />
        </MetaIconButton>
      ) : null}

      {locationLabel ? (
        <MetaIconButton label={locationLabel} testId="company-location-icon">
          <PlaceOutlinedIcon sx={META_ICON_SX} />
        </MetaIconButton>
      ) : null}

      <CompanySocialsMenu social={social} />
    </Box>
  );
}

CompanyLinksRow.propTypes = {
  website: PropTypes.string,
  email: PropTypes.string,
  locationLabel: PropTypes.string,
  social: PropTypes.oneOfType([
    PropTypes.shape({
      linkedinUrl: PropTypes.string,
      facebookUrl: PropTypes.string,
      twitterUrl: PropTypes.string,
      instagramUrl: PropTypes.string,
    }),
    PropTypes.array,
  ]),
};

/**
 * @deprecated Use CompanySocialsMenu / CompanyLinksRow `social` prop.
 * Kept as an alias so older imports keep resolving during the transition.
 */
export function CompanySocialIcons({ social }) {
  return <CompanySocialsMenu social={social} />;
}

CompanySocialIcons.propTypes = CompanySocialsMenu.propTypes;
